import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useUserFlags } from '../../hooks/useUserFlags';
import {
  copyProjectToOwner,
  createProject,
  deleteProject,
  getProjects,
  getSharedProjects,
  importProject,
  ProjectTemplate,
  updateProject,
} from '../../services/projects';
import { Project, PROJECT_DOMAIN_OPTIONS, SharedProject } from '../../types';
import {
  ArrowLeft,
  ChevronRight,
  Copy,
  FileText,
  FolderOpen,
  LayoutGrid,
  List,
  Loader2,
  LogOut,
  MoreVertical,
  Pencil,
  Plus,
  Search,
  Shield,
  Trash2,
  Upload,
  X,
} from 'lucide-react';
import { ProjectThumbnail } from '../../components/ProjectThumbnail';
import { Logo } from '../../components/Logo';
import { ConfirmModal } from '../../components/ConfirmModal';

const UNFILED_FOLDER_KEY = '__unfiled__';

function normalizeFolderName(folder: string | null | undefined): string | null {
  const trimmed = folder?.trim();
  return trimmed ? trimmed : null;
}

function matchesProjectSearch(project: Project, query: string): boolean {
  const normalizedQuery = query.trim().toLowerCase();
  if (!normalizedQuery) return true;

  return (
    project.title.toLowerCase().includes(normalizedQuery) ||
    (project.folder ?? '').toLowerCase().includes(normalizedQuery)
  );
}

function buildCopyTitle(title: string, existingTitles: Set<string>): string {
  const baseTitle = `Copy of ${title}`;
  if (!existingTitles.has(baseTitle.toLowerCase())) {
    return baseTitle;
  }

  let suffix = 2;
  while (existingTitles.has(`${baseTitle} (${suffix})`.toLowerCase())) {
    suffix += 1;
  }
  return `${baseTitle} (${suffix})`;
}

interface OwnedProjectSection {
  key: string;
  label: string;
  projects: Project[];
}

export function ProjectsPage() {
  const [projects, setProjects] = useState<Project[]>([]);
  const [sharedProjects, setSharedProjects] = useState<SharedProject[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingShared, setLoadingShared] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newProjectTitle, setNewProjectTitle] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState<ProjectTemplate>('basic');
  const [selectedDomain, setSelectedDomain] = useState<string>('');
  const [creating, setCreating] = useState(false);
  const [importing, setImporting] = useState(false);
  const [activeMenu, setActiveMenu] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingTitle, setEditingTitle] = useState('');
  const [activeTab, setActiveTab] = useState<'owned' | 'shared'>('owned');
  const [viewMode, setViewMode] = useState<'grid' | 'list' | null>(null);
  const [movingProject, setMovingProject] = useState<Project | null>(null);
  const [folderDraft, setFolderDraft] = useState('');
  const [copyingProjectId, setCopyingProjectId] = useState<string | null>(null);
  const [openFolder, setOpenFolder] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; title: string } | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const { currentUser, logout } = useAuth();
  const userFlags = useUserFlags(currentUser && !currentUser.isAnonymous ? currentUser.uid : null);
  const navigate = useNavigate();

  useEffect(() => {
    async function loadProjects() {
      if (!currentUser) return;

      setLoading(true);
      setLoadingShared(true);
      try {
        const [owned, shared] = await Promise.all([
          getProjects(currentUser.uid),
          getSharedProjects(currentUser.uid),
        ]);
        setProjects(owned);
        setSharedProjects(shared);
      } catch (error) {
        console.error('Failed to load projects:', error);
      } finally {
        setLoading(false);
        setLoadingShared(false);
      }
    }

    loadProjects();
  }, [currentUser]);

  useEffect(() => {
    if (!loading && viewMode === null) {
      setViewMode(projects.length >= 5 ? 'list' : 'grid');
    }
  }, [loading, projects.length, viewMode]);

  const effectiveViewMode = viewMode ?? 'grid';

  const filteredProjects = useMemo(
    () => projects.filter((project) => matchesProjectSearch(project, searchQuery)),
    [projects, searchQuery]
  );

  const filteredShared = useMemo(() => {
    const normalizedQuery = searchQuery.trim().toLowerCase();
    if (!normalizedQuery) return sharedProjects;

    return sharedProjects.filter((project) =>
      project.title.toLowerCase().includes(normalizedQuery)
    );
  }, [sharedProjects, searchQuery]);

  const folderOptions = useMemo(
    () =>
      Array.from(
        new Set(
          projects
            .map((project) => normalizeFolderName(project.folder))
            .filter((folder): folder is string => Boolean(folder))
        )
      ).sort((left, right) => left.localeCompare(right)),
    [projects]
  );

  const ownedProjectSections = useMemo<OwnedProjectSection[]>(() => {
    const groupedProjects = new Map<string, Project[]>();

    filteredProjects.forEach((project) => {
      const folderKey = normalizeFolderName(project.folder) ?? UNFILED_FOLDER_KEY;
      const sectionProjects = groupedProjects.get(folderKey) ?? [];
      sectionProjects.push(project);
      groupedProjects.set(folderKey, sectionProjects);
    });

    return Array.from(groupedProjects.entries())
      .sort(([left], [right]) => {
        if (left === UNFILED_FOLDER_KEY) return 1;
        if (right === UNFILED_FOLDER_KEY) return -1;
        return left.localeCompare(right);
      })
      .map(([key, sectionProjects]) => ({
        key,
        label: key === UNFILED_FOLDER_KEY ? 'Unfiled' : key,
        projects: [...sectionProjects].sort(
          (left, right) => right.updatedAt.getTime() - left.updatedAt.getTime()
        ),
      }));
  }, [filteredProjects]);

  async function handleCreateProject() {
    if (!currentUser || !newProjectTitle.trim()) return;
    if (selectedTemplate === 'open-problems' && !selectedDomain) return;

    setCreating(true);
    try {
      const domainOption = PROJECT_DOMAIN_OPTIONS.find((opt) => opt.label === selectedDomain);
      const tags = domainOption ? [domainOption.domain] : selectedDomain ? [selectedDomain] : [];
      const effectiveTemplate = selectedTemplate === 'open-problems' && domainOption?.verifier === 'lean'
        ? 'open-problems-lean' as const
        : selectedTemplate;
      const projectId = await createProject(
        currentUser.uid,
        newProjectTitle.trim(),
        effectiveTemplate,
        tags
      );
      setShowCreateModal(false);
      setNewProjectTitle('');
      setSelectedTemplate('basic');
      setSelectedDomain('');
      navigate(`/project/${projectId}`);
    } catch (error) {
      console.error('Failed to create project:', error);
    } finally {
      setCreating(false);
    }
  }

  async function handleImportFile(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    if (!file || !currentUser) return;

    setImporting(true);
    try {
      const content = await file.text();
      const fileName = file.name;
      const title = fileName.replace(/\.tex$/i, '');
      const projectId = await importProject(currentUser.uid, title, fileName, content);
      navigate(`/project/${projectId}`);
    } catch (error) {
      console.error('Failed to import file:', error);
    } finally {
      setImporting(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  }

  function handleDeleteProject(projectId: string) {
    const project = projects.find((p) => p.id === projectId);
    setDeleteTarget({ id: projectId, title: project?.title ?? 'this project' });
    setActiveMenu(null);
  }

  async function confirmDeleteProject() {
    if (!deleteTarget) return;
    try {
      await deleteProject(deleteTarget.id);
      setProjects((currentProjects) =>
        currentProjects.filter((project) => project.id !== deleteTarget.id)
      );
    } catch (error) {
      console.error('Failed to delete project:', error);
    }
    setDeleteTarget(null);
  }

  async function handleRenameProject(projectId: string) {
    if (!editingTitle.trim()) return;

    const updatedAt = new Date();

    try {
      await updateProject(projectId, { title: editingTitle.trim() });
      setProjects((currentProjects) =>
        currentProjects.map((project) =>
          project.id === projectId
            ? { ...project, title: editingTitle.trim(), updatedAt }
            : project
        )
      );
      setEditingId(null);
      setEditingTitle('');
    } catch (error) {
      console.error('Failed to rename project:', error);
    }
  }

  function startEditing(project: Project) {
    setEditingId(project.id);
    setEditingTitle(project.title);
    setActiveMenu(null);
  }

  function openMoveFolderModal(project: Project) {
    setMovingProject(project);
    setFolderDraft(project.folder ?? '');
    setActiveMenu(null);
  }

  async function handleMoveProjectToFolder() {
    if (!movingProject) return;

    const normalizedFolder = normalizeFolderName(folderDraft);
    const updatedAt = new Date();

    try {
      await updateProject(movingProject.id, { folder: normalizedFolder });
      setProjects((currentProjects) =>
        currentProjects.map((project) =>
          project.id === movingProject.id
            ? { ...project, folder: normalizedFolder, updatedAt }
            : project
        )
      );
      setMovingProject(null);
      setFolderDraft('');
    } catch (error) {
      console.error('Failed to move project:', error);
    }
  }

  async function handleCopySharedProject(project: SharedProject) {
    if (!currentUser) return;

    const existingTitles = new Set(
      projects.map((ownedProject) => ownedProject.title.toLowerCase())
    );
    const nextTitle = buildCopyTitle(project.title, existingTitles);
    setCopyingProjectId(project.projectId);

    try {
      const newProjectId = await copyProjectToOwner(
        project.projectId,
        currentUser.uid,
        nextTitle
      );
      const now = new Date();
      setProjects((currentProjects) => [
        {
          id: newProjectId,
          ownerId: currentUser.uid,
          title: nextTitle,
          folder: null,
          createdAt: now,
          updatedAt: now,
        },
        ...currentProjects,
      ]);
      setActiveTab('owned');
      navigate(`/project/${newProjectId}`);
    } catch (error) {
      console.error('Failed to copy shared project:', error);
    } finally {
      setCopyingProjectId(null);
    }
  }

  async function handleLogout() {
    try {
      await logout();
      navigate('/');
    } catch (error) {
      console.error('Failed to logout:', error);
    }
  }

  function renderOwnedProjectMenu(project: Project) {
    return (
      <div className="relative">
        <button
          onClick={() => setActiveMenu(activeMenu === project.id ? null : project.id)}
          className="p-1 rounded hover:bg-gray-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity"
        >
          <MoreVertical className="h-4 w-4 text-gray-500" />
        </button>

        {activeMenu === project.id && (
          <div className="absolute right-0 mt-1 w-44 bg-white border border-gray-200 rounded-lg shadow-lg z-10">
            <button
              onClick={() => startEditing(project)}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
            >
              <Pencil className="h-4 w-4" />
              Rename
            </button>
            <button
              onClick={() => openMoveFolderModal(project)}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
            >
              <FolderOpen className="h-4 w-4" />
              Move to folder
            </button>
            <button
              onClick={() => handleDeleteProject(project.id)}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50"
            >
              <Trash2 className="h-4 w-4" />
              Delete
            </button>
          </div>
        )}
      </div>
    );
  }

  function renderSharedProjectCopyButton(project: SharedProject) {
    const isCopying = copyingProjectId === project.projectId;

    return (
      <button
        onClick={(event) => {
          event.stopPropagation();
          handleCopySharedProject(project);
        }}
        disabled={isCopying}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {isCopying ? (
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
        ) : (
          <Copy className="h-3.5 w-3.5" />
        )}
        Copy
      </button>
    );
  }

  function renderGridSection(section: OwnedProjectSection) {
    return (
      <section key={section.key} className="space-y-3">
        <div className="flex items-center gap-2">
          <FolderOpen className="h-4 w-4 text-gray-400" />
          <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500">
            {section.label}
          </h2>
          <span className="text-xs text-gray-400">
            {section.projects.length}
          </span>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {section.projects.map((project) => (
            <div
              key={project.id}
              className="bg-white border border-gray-200 rounded-lg hover:shadow-md transition-shadow group"
            >
              <ProjectThumbnail
                projectId={project.id}
                onClick={() => navigate(`/project/${project.id}`)}
              />

              <div className="p-4">
                {editingId === project.id ? (
                  <input
                    type="text"
                    value={editingTitle}
                    onChange={(event) => setEditingTitle(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') handleRenameProject(project.id);
                      if (event.key === 'Escape') {
                        setEditingId(null);
                        setEditingTitle('');
                      }
                    }}
                    onBlur={() => handleRenameProject(project.id)}
                    autoFocus
                    className="w-full px-2 py-1 border border-primary rounded focus:ring-2 focus:ring-primary focus:outline-none"
                  />
                ) : (
                  <div className="flex items-start justify-between gap-3">
                    <div
                      onClick={() => navigate(`/project/${project.id}`)}
                      className="cursor-pointer flex-1 min-w-0"
                    >
                      <h3 className="font-medium text-gray-900 truncate">
                        {project.title}
                      </h3>
                      <p className="text-sm text-gray-500">
                        {project.updatedAt?.toLocaleDateString()}
                      </p>
                      {project.folder && (
                        <p className="text-xs text-gray-400 mt-1">
                          {project.folder}
                        </p>
                      )}
                    </div>
                    {renderOwnedProjectMenu(project)}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </section>
    );
  }

  function renderGridSharedProjects() {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {filteredShared.map((project) => (
          <div
            key={project.projectId}
            className="bg-white border border-gray-200 rounded-lg hover:shadow-md transition-shadow group"
          >
            <ProjectThumbnail
              projectId={project.projectId}
              onClick={() => navigate(`/project/${project.projectId}`)}
            />

            <div className="p-4">
              <div className="flex items-start justify-between gap-3">
                <div
                  onClick={() => navigate(`/project/${project.projectId}`)}
                  className="cursor-pointer flex-1 min-w-0"
                >
                  <h3 className="font-medium text-gray-900 truncate">
                    {project.title}
                  </h3>
                  <p className="text-sm text-gray-500">
                    {project.updatedAt?.toLocaleDateString()}
                  </p>
                  <p className="text-xs text-gray-400 mt-1">
                    Shared • {project.role}
                  </p>
                </div>
                {renderSharedProjectCopyButton(project)}
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  function renderListProjectRow(project: Project) {
    return (
      <div
        key={project.id}
        className="flex items-center gap-4 px-4 py-3 hover:bg-gray-50 transition-colors group"
      >
        <FileText className="h-5 w-5 text-gray-400 flex-shrink-0" />
        <div
          onClick={() => navigate(`/project/${project.id}`)}
          className="flex-1 min-w-0 cursor-pointer"
        >
          {editingId === project.id ? (
            <input
              type="text"
              value={editingTitle}
              onChange={(event) => setEditingTitle(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') handleRenameProject(project.id);
                if (event.key === 'Escape') {
                  setEditingId(null);
                  setEditingTitle('');
                }
              }}
              onBlur={() => handleRenameProject(project.id)}
              onClick={(event) => event.stopPropagation()}
              autoFocus
              className="w-full px-2 py-1 border border-primary rounded focus:ring-2 focus:ring-primary focus:outline-none text-sm"
            />
          ) : (
            <h3 className="font-medium text-gray-900 truncate text-sm">
              {project.title}
            </h3>
          )}
        </div>
        <span className="text-sm text-gray-500 flex-shrink-0 hidden sm:block">
          {project.updatedAt?.toLocaleDateString()}
        </span>
        <div className="flex-shrink-0">
          {renderOwnedProjectMenu(project)}
        </div>
      </div>
    );
  }

  function renderListOwnedProjects() {
    // When searching, show flat results across all folders (no drill-down)
    if (searchQuery.trim()) {
      return (
        <div className="bg-white border border-gray-200 rounded-lg">
          <div className="divide-y divide-gray-200">
            {filteredProjects.map((project) => renderListProjectRow(project))}
          </div>
        </div>
      );
    }

    // Drill-down: inside a folder
    if (openFolder !== null) {
      const section = ownedProjectSections.find((s) => s.key === openFolder);
      if (!section) return null;

      return (
        <div className="bg-white border border-gray-200 rounded-lg">
          <div className="flex items-center gap-2 px-4 py-3 bg-gray-50 border-b border-gray-200">
            <button
              type="button"
              onClick={() => setOpenFolder(null)}
              className="p-1 -ml-1 rounded hover:bg-gray-200 text-gray-500 hover:text-gray-700"
              title="Back to folders"
            >
              <ArrowLeft className="h-4 w-4" />
            </button>
            <FolderOpen className="h-4 w-4 text-primary" />
            <h2 className="text-sm font-semibold text-gray-700">
              {section.label}
            </h2>
            <span className="text-xs text-gray-400">
              {section.projects.length} {section.projects.length === 1 ? 'project' : 'projects'}
            </span>
          </div>
          <div className="divide-y divide-gray-200">
            {section.projects.map((project) => renderListProjectRow(project))}
          </div>
        </div>
      );
    }

    // Root level: show folders as rows
    return (
      <div className="bg-white border border-gray-200 rounded-lg">
        <div className="divide-y divide-gray-200">
          {ownedProjectSections.map((section) => (
            <div
              key={section.key}
              onClick={() => setOpenFolder(section.key)}
              className="flex items-center gap-4 px-4 py-3 hover:bg-gray-50 transition-colors cursor-pointer group"
            >
              <FolderOpen className="h-5 w-5 text-amber-500 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <h3 className="font-medium text-gray-900 text-sm">
                  {section.label}
                </h3>
                <p className="text-xs text-gray-400 mt-0.5">
                  {section.projects.length} {section.projects.length === 1 ? 'project' : 'projects'}
                </p>
              </div>
              <span className="text-sm text-gray-500 flex-shrink-0 hidden sm:block">
                {section.projects[0]?.updatedAt?.toLocaleDateString()}
              </span>
              <ChevronRight className="h-4 w-4 text-gray-400 flex-shrink-0" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  function renderListSharedProjects() {
    return (
      <div className="bg-white border border-gray-200 rounded-lg divide-y divide-gray-200">
        {filteredShared.map((project) => (
          <div
            key={project.projectId}
            className="flex items-center gap-4 px-4 py-3 hover:bg-gray-50 transition-colors"
          >
            <FileText className="h-5 w-5 text-gray-400 flex-shrink-0" />
            <div
              onClick={() => navigate(`/project/${project.projectId}`)}
              className="flex-1 min-w-0 cursor-pointer"
            >
              <h3 className="font-medium text-gray-900 truncate text-sm">
                {project.title}
              </h3>
            </div>
            <span className="text-xs text-gray-400 flex-shrink-0 hidden sm:block">
              {project.role}
            </span>
            <span className="text-sm text-gray-500 flex-shrink-0 hidden sm:block">
              {project.updatedAt?.toLocaleDateString()}
            </span>
            <div className="flex-shrink-0">
              {renderSharedProjectCopyButton(project)}
            </div>
          </div>
        ))}
      </div>
    );
  }

  const isOwnedEmpty = activeTab === 'owned' && filteredProjects.length === 0;
  const isSharedEmpty = activeTab === 'shared' && filteredShared.length === 0;

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <FileText className="h-8 w-8 text-primary" />
            <Logo className="text-xl font-bold text-gray-900" />
          </div>
          <div className="flex items-center gap-2 sm:gap-4">
            {(userFlags.isAdmin || userFlags.isOwner) && (
              <button
                onClick={() => navigate('/admin')}
                className="flex items-center gap-1.5 px-2 sm:px-3 py-1.5 text-sm font-medium text-gray-600 hover:text-gray-900 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                <Shield className="h-4 w-4" />
                <span className="hidden sm:inline">Console</span>
              </button>
            )}
            <span className="hidden sm:inline text-sm text-gray-600">
              {currentUser?.displayName || currentUser?.email}
            </span>
            <button
              onClick={handleLogout}
              className="flex items-center gap-1 text-gray-600 hover:text-gray-900"
            >
              <LogOut className="h-5 w-5" />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Projects</h1>
            <div className="flex gap-6 mt-2 border-b border-gray-200">
              <button
                onClick={() => { setActiveTab('owned'); setOpenFolder(null); }}
                className={`pb-2 text-sm font-medium ${
                  activeTab === 'owned'
                    ? 'text-primary border-b-2 border-primary'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Your Projects
              </button>
              <button
                onClick={() => { setActiveTab('shared'); setOpenFolder(null); }}
                className={`pb-2 text-sm font-medium ${
                  activeTab === 'shared'
                    ? 'text-primary border-b-2 border-primary'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Shared With Me
              </button>
            </div>
          </div>

          <div className="flex gap-3 w-full sm:w-auto">
            <div className="flex border border-gray-300 rounded-lg overflow-hidden">
              <button
                onClick={() => { setViewMode('grid'); setOpenFolder(null); }}
                className={`p-2 ${
                  effectiveViewMode === 'grid'
                    ? 'bg-primary text-white'
                    : 'bg-white text-gray-500 hover:bg-gray-50'
                }`}
                aria-label="Grid view"
                title="Grid view"
              >
                <LayoutGrid className="h-4 w-4" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 ${
                  effectiveViewMode === 'list'
                    ? 'bg-primary text-white'
                    : 'bg-white text-gray-500 hover:bg-gray-50'
                }`}
                aria-label="List view"
                title="List view"
              >
                <List className="h-4 w-4" />
              </button>
            </div>

            <div className="relative flex-1 sm:flex-none">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="search"
                placeholder={
                  activeTab === 'owned'
                    ? 'Search projects or folders...'
                    : 'Search shared projects...'
                }
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                aria-label="Search projects"
                autoComplete="off"
                className="w-full sm:w-64 pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>

            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={importing}
              className="flex items-center gap-2 px-2 sm:px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              title="Import .tex file"
            >
              {importing ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Upload className="h-4 w-4" />
              )}
              <span className="hidden sm:inline">Import</span>
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".tex"
              onChange={handleImportFile}
              className="hidden"
            />

            <button
              onClick={() => setShowCreateModal(true)}
              className="flex items-center gap-2 px-2 sm:px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-hover"
              title="New project"
            >
              <Plus className="h-4 w-4" />
              <span className="hidden sm:inline">New</span>
            </button>
          </div>
        </div>

        {activeTab === 'owned' && folderOptions.length > 0 && effectiveViewMode === 'grid' && (
          <div className="mb-6 flex flex-wrap gap-2">
            {folderOptions.map((folder) => (
              <button
                key={folder}
                type="button"
                onClick={() => setSearchQuery(folder)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 bg-white border border-gray-200 rounded-full hover:border-gray-300 hover:text-gray-900"
              >
                <FolderOpen className="h-3.5 w-3.5" />
                {folder}
              </button>
            ))}
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery('')}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-500 border border-dashed border-gray-300 rounded-full hover:text-gray-700"
              >
                <X className="h-3.5 w-3.5" />
                Clear search
              </button>
            )}
          </div>
        )}

        {activeTab === 'owned' && loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : activeTab === 'shared' && loadingShared ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : isOwnedEmpty ? (
          <div className="text-center py-20">
            <FileText className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h2 className="text-xl font-medium text-gray-900 mb-2">
              {searchQuery ? 'No projects found' : 'No projects yet'}
            </h2>
            <p className="text-gray-600 mb-6">
              {searchQuery
                ? 'Try a different search term'
                : 'Create your first project to get started'}
            </p>
            {!searchQuery && (
              <button
                onClick={() => setShowCreateModal(true)}
                className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-hover"
              >
                <Plus className="h-4 w-4" />
                Create Project
              </button>
            )}
          </div>
        ) : isSharedEmpty ? (
          <div className="text-center py-20">
            <FileText className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h2 className="text-xl font-medium text-gray-900 mb-2">
              {searchQuery ? 'No projects found' : 'No shared projects yet'}
            </h2>
            <p className="text-gray-600">
              {searchQuery
                ? 'Try a different search term'
                : 'Shared projects will appear here'}
            </p>
          </div>
        ) : activeTab === 'owned' ? (
          effectiveViewMode === 'grid' ? (
            <div className="space-y-8">
              {ownedProjectSections.map((section) => renderGridSection(section))}
            </div>
          ) : (
            renderListOwnedProjects()
          )
        ) : effectiveViewMode === 'grid' ? (
          renderGridSharedProjects()
        ) : (
          renderListSharedProjects()
        )}
      </main>

      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <h2 className="text-xl font-bold text-gray-900 mb-4">New Project</h2>

            <label className="block text-sm font-medium text-gray-700 mb-2">Template</label>
            <div className="grid grid-cols-2 gap-3 mb-4">
              <button
                type="button"
                onClick={() => setSelectedTemplate('basic')}
                className={`p-4 border-2 rounded-lg text-left transition-colors ${
                  selectedTemplate === 'basic'
                    ? 'border-primary bg-primary/5'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <FileText className={`h-6 w-6 mb-2 ${selectedTemplate === 'basic' ? 'text-primary' : 'text-gray-400'}`} />
                <div className="font-medium text-sm text-gray-900">Basic</div>
                <div className="text-xs text-gray-500 mt-1">Simple LaTeX document with bibliography</div>
              </button>
              <button
                type="button"
                onClick={() => setSelectedTemplate('open-problems')}
                className={`p-4 border-2 rounded-lg text-left transition-colors ${
                  selectedTemplate === 'open-problems'
                    ? 'border-primary bg-primary/5'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <FileText className={`h-6 w-6 mb-2 ${selectedTemplate === 'open-problems' ? 'text-primary' : 'text-gray-400'}`} />
                <div className="font-medium text-sm text-gray-900">Open Problems</div>
                <div className="text-xs text-gray-500 mt-1">Problem submission with prompt, verifier &amp; questions</div>
              </button>
            </div>

            {selectedTemplate === 'open-problems' && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Domain</label>
                <select
                  value={selectedDomain}
                  onChange={(event) => setSelectedDomain(event.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent bg-white"
                >
                  <option value="">Select a domain...</option>
                  {PROJECT_DOMAIN_OPTIONS.map((opt) => (
                    <option key={opt.label} value={opt.label}>{opt.label}</option>
                  ))}
                </select>
              </div>
            )}

            <input
              type="text"
              placeholder="Project title"
              value={newProjectTitle}
              onChange={(event) => setNewProjectTitle(event.target.value)}
              onKeyDown={(event) => event.key === 'Enter' && handleCreateProject()}
              autoFocus
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent mb-4"
            />
            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setNewProjectTitle('');
                  setSelectedTemplate('basic');
                  setSelectedDomain('');
                }}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateProject}
                disabled={creating || !newProjectTitle.trim() || (selectedTemplate === 'open-problems' && !selectedDomain)}
                className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-hover disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {creating && <Loader2 className="h-4 w-4 animate-spin" />}
                Create
              </button>
            </div>
          </div>
        </div>
      )}

      {movingProject && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex items-center gap-2 mb-4">
              <FolderOpen className="h-5 w-5 text-primary" />
              <h2 className="text-xl font-bold text-gray-900">Move to folder</h2>
            </div>

            <p className="text-sm text-gray-600 mb-4">
              Choose a folder for <span className="font-medium text-gray-900">{movingProject.title}</span>.
              Leave it blank to keep the project unfiled.
            </p>

            <input
              type="text"
              list="project-folder-options"
              placeholder="Folder name"
              value={folderDraft}
              onChange={(event) => setFolderDraft(event.target.value)}
              onKeyDown={(event) => event.key === 'Enter' && handleMoveProjectToFolder()}
              autoFocus
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent mb-3"
            />
            <datalist id="project-folder-options">
              {folderOptions.map((folder) => (
                <option key={folder} value={folder} />
              ))}
            </datalist>

            {folderOptions.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-4">
                {folderOptions.map((folder) => (
                  <button
                    key={folder}
                    type="button"
                    onClick={() => setFolderDraft(folder)}
                    className="px-3 py-1.5 text-sm text-gray-600 bg-gray-50 border border-gray-200 rounded-full hover:border-gray-300"
                  >
                    {folder}
                  </button>
                ))}
              </div>
            )}

            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setMovingProject(null);
                  setFolderDraft('');
                }}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={handleMoveProjectToFolder}
                className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-hover"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {activeMenu && (
        <div
          className="fixed inset-0 z-0"
          onClick={() => setActiveMenu(null)}
        />
      )}

      <ConfirmModal
        open={deleteTarget !== null}
        title="Delete project"
        message={`Are you sure you want to delete "${deleteTarget?.title ?? ''}"? This cannot be undone.`}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        variant="danger"
        onConfirm={confirmDeleteProject}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}
